import * as Dialog from '@radix-ui/react-dialog'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Drawer — side panel for detail consulted next to its list (audit row,
// contact) without leaving the task (design/components/overlays.html §5.1).
// Built on Radix Dialog (focus in/out, Esc). Enters from the right on desktop
// or the bottom (sheet) on mobile.

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  side?: 'right' | 'bottom'
  /** Right-side width in px (default 300). */
  width?: number
  children: ReactNode
}

/** Side panel (Radix Dialog: focus trap, Esc, overlay click close) for detail
 *  consulted next to its list without leaving the task. `side` picks right
 *  slide-in (desktop, `width` px) or bottom sheet (mobile, max 80vh). */
export function Drawer({
  open,
  onClose,
  title,
  side = 'right',
  width = 300,
  children,
}: DrawerProps) {
  const { t } = useTranslation('components')
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(8,10,14,0.42)]" />
        <Dialog.Content
          aria-describedby={undefined}
          style={side === 'right' ? { width: `${width}px` } : undefined}
          className={cn(
            'fixed z-50 flex flex-col bg-surface shadow-modal focus:outline-none',
            side === 'right'
              ? 'bottom-0 right-0 top-0 max-w-[calc(100vw-2rem)] border-l border-line'
              : 'bottom-0 left-0 right-0 max-h-[80vh] rounded-t-xl border-t border-line',
          )}
        >
          <div className="flex flex-none items-center gap-2.5 border-b border-line px-[18px] py-4">
            <Dialog.Title className="text-[14px] font-bold text-text">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t('ui.close')}
                className="ml-auto flex size-7 flex-none items-center justify-center rounded-sm text-text-3 transition-colors hover:bg-hover hover:text-text [&_svg]:size-[15px]"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-[18px] py-4 text-[12px] leading-relaxed text-text-2">
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
